(() => {
  const config = window.themeCurrencyConverterConfig || {};

  const BASE_CURRENCY = String(config.baseCurrency || 'EUR').toUpperCase();
  const DISPLAY_CURRENCY_DZD = 'DZD';
  const GEO_API_ENDPOINT = 'https://ipwho.is/?fields=success,country_code';
  const RATE_API_ENDPOINT = `https://open.er-api.com/v6/latest/${BASE_CURRENCY}`;

  const RATE_CACHE_TTL_MS = 12 * 60 * 60 * 1000;
  const GEO_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

  const storageKeys = {
    rate: `tausil_rate_${BASE_CURRENCY.toLowerCase()}_dzd_v1`,
    geo: 'tausil_geo_country_v1',
  };

  const fallbackRate = Number(config.fallbackRate) > 0 ? Number(config.fallbackRate) : BASE_CURRENCY === 'EUR' ? 150 : 1;

  const EURO_PRICE_PATTERN =
    /(?:(?:EUR\s*)?-?€\s*-?[0-9][0-9\s.,]*(?:\s*EUR\b)?|(?:EUR\s*)?-?[0-9][0-9\s.,]*\s*€(?:\s*EUR\b)?|-?[0-9][0-9\s.,]*\s*EUR\b|EUR\b\s*-?[0-9][0-9\s.,]*)/gi;

  const ignoredParentTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'OPTION']);
  const textNodeState = new WeakMap();

  let currentDisplayCurrency = BASE_CURRENCY;
  let conversionRate = null;
  let isApplying = false;
  let applyQueued = false;
  let observer = null;
  let checkoutNoteStyleMounted = false;

  function safeLocalStorageGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeLocalStorageSet(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      return;
    }
  }

  function readStoredObject(key) {
    const raw = safeLocalStorageGet(key);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function writeStoredObject(key, value) {
    safeLocalStorageSet(key, JSON.stringify(value));
  }

  function getCurrentLanguageCode() {
    return String(document.documentElement.lang || config.locale || '').toLowerCase();
  }

  function containsEuroCurrencyToken(text) {
    return /€|EUR\b/i.test(text);
  }

  function parseLocalizedAmount(value) {
    const compact = value.replace(/EUR/gi, '').replace(/€/g, '').replace(/[\s\u00A0\u202F]/g, '');
    if (!compact) return Number.NaN;

    const isNegative = compact.includes('-');
    const unsigned = compact.replace(/-/g, '');
    if (!unsigned) return Number.NaN;

    const lastComma = unsigned.lastIndexOf(',');
    const lastDot = unsigned.lastIndexOf('.');
    let decimalSeparator = '';

    if (lastComma !== -1 && lastDot !== -1) {
      decimalSeparator = lastComma > lastDot ? ',' : '.';
    } else if (lastComma !== -1) {
      const decimals = unsigned.length - lastComma - 1;
      decimalSeparator = decimals > 0 && decimals <= 2 ? ',' : '';
    } else if (lastDot !== -1) {
      const decimals = unsigned.length - lastDot - 1;
      decimalSeparator = decimals > 0 && decimals <= 2 ? '.' : '';
    }

    let normalized = unsigned;
    if (decimalSeparator) {
      const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
      normalized = normalized.split(thousandsSeparator).join('');
      normalized = normalized.replace(decimalSeparator, '.');
    } else {
      normalized = normalized.replace(/[.,]/g, '');
    }

    const amount = Number(normalized);
    if (!Number.isFinite(amount)) return Number.NaN;
    return isNegative ? -amount : amount;
  }

  function formatDzdAmount(amount) {
    const locale = getCurrentLanguageCode().startsWith('ar') ? 'ar-DZ' : 'fr-DZ';
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: DISPLAY_CURRENCY_DZD,
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

    // Keep currency code + amount ordering stable in RTL contexts.
    return getCurrentLanguageCode().startsWith('ar') ? `\u2068${formatted}\u2069` : formatted;
  }

  function convertEuroTextToDzd(text) {
    EURO_PRICE_PATTERN.lastIndex = 0;
    if (!EURO_PRICE_PATTERN.test(text)) return text;

    EURO_PRICE_PATTERN.lastIndex = 0;
    return text.replace(EURO_PRICE_PATTERN, (match) => {
      const eurAmount = parseLocalizedAmount(match);
      if (!Number.isFinite(eurAmount)) return match;
      return formatDzdAmount(eurAmount * conversionRate);
    });
  }

  function getNodeState(textNode) {
    const existingState = textNodeState.get(textNode);
    if (existingState) return existingState;

    const initialState = {
      eurText: textNode.textContent,
      dzdText: null,
    };
    textNodeState.set(textNode, initialState);
    return initialState;
  }

  function processTextNode(textNode) {
    const parent = textNode.parentElement;
    if (!parent || ignoredParentTags.has(parent.tagName) || parent.closest('[data-currency-converter-ignore]')) return;

    const currentText = textNode.textContent;
    if (!currentText || !currentText.trim()) return;

    const state = getNodeState(textNode);

    if (containsEuroCurrencyToken(currentText) && currentText !== state.eurText) {
      state.eurText = currentText;
      state.dzdText = null;
    }

    if (currentDisplayCurrency === BASE_CURRENCY) {
      if (currentText !== state.eurText) {
        textNode.textContent = state.eurText;
      }
      return;
    }

    if (!Number.isFinite(conversionRate) || conversionRate <= 0 || !containsEuroCurrencyToken(state.eurText)) return;

    const nextDzdText = convertEuroTextToDzd(state.eurText);
    state.dzdText = nextDzdText;

    if (currentText !== nextDzdText) {
      textNode.textContent = nextDzdText;
    }
  }

  function applyCurrency(rootNode = document.body) {
    if (!rootNode || isApplying) return;

    isApplying = true;
    try {
      const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.textContent || !node.textContent.trim() || !node.parentElement) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      const textNodes = [];
      let currentNode = walker.nextNode();
      while (currentNode) {
        textNodes.push(currentNode);
        currentNode = walker.nextNode();
      }

      textNodes.forEach(processTextNode);
      upsertCheckoutCurrencyNotes();

      document.documentElement.setAttribute('data-display-currency', currentDisplayCurrency);
      document.dispatchEvent(
        new CustomEvent('theme:currency-updated', {
          detail: {
            currency: currentDisplayCurrency,
            rate: conversionRate,
          },
        })
      );
    } finally {
      isApplying = false;
    }
  }

  function queueApplyCurrency() {
    if (applyQueued) return;
    applyQueued = true;
    requestAnimationFrame(() => {
      applyQueued = false;
      applyCurrency(document.body);
    });
  }

  function getCheckoutCurrencyMessage() {
    if (currentDisplayCurrency !== DISPLAY_CURRENCY_DZD) return '';
    if (getCurrentLanguageCode().startsWith('ar')) {
      return 'الأسعار معروضة بالدينار الجزائري (DZD). عند إتمام الطلب قد تظهر صفحة الدفع باليورو (EUR). الدفع عند الاستلام داخل الجزائر يكون بالدينار الجزائري.';
    }
    return 'Prices are shown in DZD. At checkout, some steps might display EUR. For Algeria, cash on delivery should be paid in DZD.';
  }

  function ensureCheckoutNoteStyle() {
    if (checkoutNoteStyleMounted || !document.head) return;

    const styleTag = document.createElement('style');
    styleTag.id = 'currency-checkout-note-style';
    styleTag.textContent = `
      .currency-checkout-note {
        margin: 0.8rem 0 0;
        font-size: 1.2rem;
        line-height: 1.45;
        color: rgba(var(--color-foreground), 0.75);
      }
    `;

    document.head.appendChild(styleTag);
    checkoutNoteStyleMounted = true;
  }

  function getCheckoutContexts() {
    return Array.from(
      document.querySelectorAll(
        [
          'cart-drawer',
          'cart-items',
          '#main-cart-footer',
          '.cart__footer',
          '.cart-drawer__footer',
          '.drawer__footer',
          '#CartDrawer',
        ].join(', ')
      )
    );
  }

  function upsertCheckoutCurrencyNotes() {
    const message = getCheckoutCurrencyMessage();
    const checkoutContexts = getCheckoutContexts();

    document.querySelectorAll('.currency-checkout-note').forEach((node) => {
      if (!checkoutContexts.includes(node.closest('cart-drawer, cart-items, #main-cart-footer, .cart__footer, .cart-drawer__footer, .drawer__footer, #CartDrawer'))) {
        node.remove();
      }
    });

    if (!message) {
      checkoutContexts.forEach((context) => {
        context.querySelectorAll('.currency-checkout-note').forEach((node) => node.remove());
      });
      return;
    }

    ensureCheckoutNoteStyle();

    checkoutContexts.forEach((context) => {
      const checkoutTrigger = context.querySelector('button[name="checkout"], a[href*="/checkout"]');
      if (!checkoutTrigger) return;

      const noteContainer =
        checkoutTrigger.closest('.cart__ctas, .cart__blocks, .drawer__footer, .cart-drawer__footer, form') ||
        checkoutTrigger.parentElement ||
        context;
      if (!noteContainer) return;

      let noteNode = noteContainer.querySelector(':scope > .currency-checkout-note');
      if (!noteNode) {
        noteNode = document.createElement('p');
        noteNode.className = 'currency-checkout-note';
        noteContainer.appendChild(noteNode);
      }

      noteNode.textContent = message;
    });
  }

  function observeDomChanges() {
    if (observer || !document.body) return;

    observer = new MutationObserver((mutations) => {
      if (isApplying) return;

      for (const mutation of mutations) {
        if (
          (mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) ||
          mutation.type === 'characterData'
        ) {
          queueApplyCurrency();
          return;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  function readCachedRate() {
    const cached = readStoredObject(storageKeys.rate);
    if (!cached?.rate || !cached.timestamp) return null;
    if (Date.now() - cached.timestamp > RATE_CACHE_TTL_MS) return null;

    const parsedRate = Number(cached.rate);
    return Number.isFinite(parsedRate) && parsedRate > 0 ? parsedRate : null;
  }

  function writeCachedRate(rate) {
    writeStoredObject(storageKeys.rate, {
      rate,
      timestamp: Date.now(),
    });
  }

  async function fetchLatestRate() {
    try {
      const response = await fetch(RATE_API_ENDPOINT, { cache: 'no-store' });
      if (!response.ok) return null;

      const payload = await response.json();
      const latestRate = Number(payload?.rates?.[DISPLAY_CURRENCY_DZD]);
      if (!Number.isFinite(latestRate) || latestRate <= 0) return null;

      writeCachedRate(latestRate);
      return latestRate;
    } catch (error) {
      return null;
    }
  }

  function readCachedGeoCountry() {
    const cached = readStoredObject(storageKeys.geo);
    if (!cached?.countryCode || !cached.timestamp) return null;
    if (Date.now() - cached.timestamp > GEO_CACHE_TTL_MS) return null;
    return String(cached.countryCode).toUpperCase();
  }

  function writeCachedGeoCountry(countryCode) {
    writeStoredObject(storageKeys.geo, {
      countryCode,
      timestamp: Date.now(),
    });
  }

  async function fetchGeoCountryCode() {
    const cachedCountryCode = readCachedGeoCountry();
    if (cachedCountryCode) return cachedCountryCode;

    try {
      const response = await fetch(GEO_API_ENDPOINT, { cache: 'no-store' });
      if (!response.ok) return null;

      const payload = await response.json();
      if (payload?.success === false) return null;

      const countryCode = String(payload?.country_code || '').toUpperCase();
      if (!countryCode) return null;

      writeCachedGeoCountry(countryCode);
      return countryCode;
    } catch (error) {
      return null;
    }
  }

  async function resolveDisplayCurrency() {
    // Rule: Arabic language OR Algerian IP => DZD. Otherwise => base currency (EUR).
    if (getCurrentLanguageCode().startsWith('ar')) return DISPLAY_CURRENCY_DZD;

    const geoCountryCode = await fetchGeoCountryCode();
    if (geoCountryCode === 'DZ') return DISPLAY_CURRENCY_DZD;

    return BASE_CURRENCY;
  }

  async function initCurrencyDisplay() {
    observeDomChanges();

    currentDisplayCurrency = await resolveDisplayCurrency();

    if (currentDisplayCurrency === BASE_CURRENCY) {
      queueApplyCurrency();
      return;
    }

    conversionRate = 154;
    queueApplyCurrency();
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCurrencyDisplay, { once: true });
  } else {
    initCurrencyDisplay();
  }
})();
